import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { BulkListDefaults } from '@/api/partners/cars/use-bulk-list-defaults';
import { Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';

export const defaultsFormSchema = z.object({
  hoursBetweenTrips: z.string().min(1, 'Hours between trips is required'),
  minHoursInAdvance: z.string().min(1, 'Minimum hours in advance is required'),
  allowSubscription: z.boolean(),
  supportsDelivery: z.boolean(),
});

export type DefaultsFormType = z.infer<typeof defaultsFormSchema>;

export default function DefaultsForm({
  defaults,
  onValidChange,
  onChange,
  defaultValues,
}: {
  defaults: BulkListDefaults;
  onValidChange: (isValid: boolean) => void;
  onChange: (values: DefaultsFormType) => void;
  defaultValues?: Partial<DefaultsFormType>;
}) {
  const { styles, theme } = useStyles(stylesheet);

  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid, errors },
    watch,
  } = useForm<DefaultsFormType>({
    defaultValues: {
      hoursBetweenTrips:
        defaultValues?.hoursBetweenTrips ?? String(defaults.hoursBetweenTrips),
      minHoursInAdvance:
        defaultValues?.minHoursInAdvance ?? String(defaults.minHoursInAdvance),
      allowSubscription:
        defaultValues?.allowSubscription ?? defaults.allowSubscription,
      supportsDelivery:
        defaultValues?.supportsDelivery ?? defaults.supportsDelivery,
    },
    resolver: zodResolver(defaultsFormSchema),
  });

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (defaultValues) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    hoursBetweenTrips,
    minHoursInAdvance,
    allowSubscription,
    supportsDelivery,
  ] = useWatch({
    control,
    name: [
      'hoursBetweenTrips',
      'minHoursInAdvance',
      'allowSubscription',
      'supportsDelivery',
    ],
  });

  const content = (
    <View style={{ flexGrow: 1, gap: 24 }}>
      <View style={{ gap: 8 }}>
        <Text type="title2">What Bulk List includes</Text>

        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          Bulk list uses defaults from your past listings and automated pricing
          to streamline the listing for all your cars. You can easily customize
          each car later in the Cars → Edit Car section
        </Text>
      </View>

      <Text type="bodyRegular">Availability</Text>

      <View style={{ gap: 24, flexGrow: 1 }}>
        <Dropdown
          mode="single"
          label="Minimum Time Between Rentals"
          options={[
            { id: '3', value: '3', title: '3 Hours' },
            { id: '6', value: '6', title: '6 Hours' },
            { id: '12', value: '12', title: '12 Hours' },
            { id: '24', value: '24', title: '24 Hours' },
          ]}
          // placeHolderText="Select your car's pickup location"
          onSelect={(selected: DropdownOption[]) => {
            setValue('hoursBetweenTrips', selected[0].id);
            trigger();
          }}
          onClear={() => {
            setValue('hoursBetweenTrips', '');
            trigger('hoursBetweenTrips');
          }}
          tooltipText="Sets a buffer between the end of one trip and the start of the next. We hold the calendar for this time so you can clean, charge, or handle handoff. Existing reservations stay as is.
          Example: 3 hours means a trip ending at 5 pm makes the next eligible start 8 pm."
          values={hoursBetweenTrips ? [hoursBetweenTrips.toString()] : []}
          error={errors.hoursBetweenTrips?.message}
        />

        <Dropdown
          mode="single"
          label="Minimum Time In Advance"
          options={[
            { id: '3', value: '3', title: '3 Hours' },
            { id: '6', value: '6', title: '6 Hours' },
            { id: '12', value: '12', title: '12 Hours' },
            { id: '24', value: '24', title: '24 Hours' },
          ]}
          onSelect={(selected: DropdownOption[]) => {
            setValue('minHoursInAdvance', selected[0].id);
            trigger();
          }}
          onClear={() => {
            setValue('minHoursInAdvance', '');
            trigger('minHoursInAdvance');
          }}
          tooltipText="Requires lead time for new bookings. Guests can only start a trip after this window from the moment they book. Higher values reduce last-minute bookings.
          Example: with 3 hours, a booking made at 2 pm cannot start before 5 pm today."
          values={minHoursInAdvance ? [minHoursInAdvance?.toString()] : []}
          error={errors.minHoursInAdvance?.message}
        />
      </View>

      <View
        style={{
          gap: 16,
          paddingBottom: 32,
        }}
      >
        <Text type="bodyRegular">Subscription and Delivery</Text>

        <CheckItem
          text="Subscriptions"
          caption="Enable this to allow guests to subscribe to your car for extended periods.
Weekly, monthly, or up to 6 months. Ideal for long-term rentals and recurring revenue."
          onPress={() => {
            setValue('allowSubscription', !allowSubscription);
            trigger();
          }}
          active={allowSubscription}
          type="checkbox"
        />

        <CheckItem
          text="Available for Delivery"
          caption="Uses the same delivery settings as your previous listing. You can always adjust them later"
          onPress={() => {
            setValue('supportsDelivery', !supportsDelivery);
            trigger();
          }}
          active={supportsDelivery}
          type="checkbox"
        />
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? content : content}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    gap: 16,
    maxWidth: {
      xs: '100%',
      md: 600,
    },
  },
}));
